package er.prateek.crossly.persistence

import com.russhwolf.settings.Settings
import er.prateek.crossly.ads.entitlement.Entitlement
import er.prateek.crossly.ads.entitlement.EntitlementSource

private const val KEY_AD_FREE = "entitlement_ad_free"
private const val KEY_SOURCE = "entitlement_source"
private const val KEY_EXPIRES_AT = "entitlement_expires_at"

/**
 * Local cache of the last known entitlement, so ad-free state survives a relaunch and an
 * offline start.
 *
 * This is a cache, never the source of truth: a store receipt is. It is deliberately not
 * tamper-proof -- on-device prefs never are -- and the honest mitigation is to have the
 * gateway re-verify on every launch, which [er.prateek.crossly.ads.entitlement.DefaultEntitlementProvider]
 * does via `refresh()`.
 */
class SettingsEntitlementStore(
    private val settings: Settings,
) : EntitlementStore {

    override fun load(): Entitlement {
        if (!settings.getBoolean(KEY_AD_FREE, false)) return Entitlement.NONE
        val source = settings.getStringOrNull(KEY_SOURCE)
            ?.let { runCatching { EntitlementSource.valueOf(it) }.getOrNull() }
            ?: EntitlementSource.NONE
        // A stored 0 means "no expiry" -- Settings has no nullable Long getter, and an
        // entitlement that genuinely expired at epoch 0 is not a case worth modelling.
        val expiresAt = settings.getLong(KEY_EXPIRES_AT, 0L).takeIf { it > 0L }
        return Entitlement(adFree = true, source = source, expiresAtEpochMs = expiresAt)
    }

    override fun save(entitlement: Entitlement) {
        settings.putBoolean(KEY_AD_FREE, entitlement.adFree)
        settings.putString(KEY_SOURCE, entitlement.source.name)
        settings.putLong(KEY_EXPIRES_AT, entitlement.expiresAtEpochMs ?: 0L)
    }
}
